// Financial planning helpers — frontend copy.
//
// Kept in lockstep with server/src/lib/financials.ts. Both files use the
// SAME formulas so Scoping preview + Project drawer + tracker push writer
// all produce identical numbers. Do not drift without updating both.

/**
 * Fractional calendar months between two dates. Partial months are pro-rated.
 * Returns 0 for invalid dates or end <= start.
 */
export function monthsBetween(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  if (!isFinite(s.getTime()) || !isFinite(e.getTime()) || e <= s) return 0;
  const ms = e.getTime() - s.getTime();
  // 30.4375 = average days per Gregorian calendar month.
  return ms / (1000 * 60 * 60 * 24 * 30.4375);
}

/**
 * Total contracted hours over the engagement.
 * monthsBetween(start, end) × monthlyBudgetHours, rounded to an int.
 */
export function computeBudgetHours(start, end, monthlyBudgetHours) {
  if (!monthlyBudgetHours || monthlyBudgetHours <= 0) return 0;
  const m = monthsBetween(start, end);
  return Math.round(m * monthlyBudgetHours);
}

/**
 * Top-line budget = total contracted hours × project-level hourly rate.
 * This is what the client is billed against; compare vs projectCost for margin.
 */
export function computeProjectBudget(budgetHours, hourlyRate) {
  return (budgetHours || 0) * (hourlyRate || 0);
}

/**
 * Cost rollup across resources. Each resource is
 *   { hourlyRate?: number, allocatedHours: number }
 * where allocatedHours is TOTAL hours across the engagement (not per month).
 *
 * Scoping callers — whose input is hours-per-month — must pre-multiply:
 *   allocatedHours = allocatedHoursPerMonth × monthsBetween(start, end)
 *
 * Project callers pass ProjectAssignment.allocatedHours directly.
 *
 * Fallback rule: a resource with hourlyRate > 0 uses its own rate;
 * null or 0 falls back to the project-level `fallbackRate`.
 */
export function computeProjectCost(resources, fallbackRate) {
  const rows = Array.isArray(resources) ? resources : [];
  return rows.reduce((sum, r) => {
    const rate = (r && r.hourlyRate > 0) ? r.hourlyRate : (fallbackRate || 0);
    const hours = (r && r.allocatedHours) || 0;
    return sum + hours * rate;
  }, 0);
}

/**
 * Margin % = (budget - cost) / budget × 100. Returns null when budget is 0.
 * Handles negative margin honestly (cost > budget).
 */
export function computeMarginPct(projectBudget, projectCost) {
  if (!projectBudget || projectBudget <= 0) return null;
  return ((projectBudget - projectCost) / projectBudget) * 100;
}
